import { useState, useEffect, useRef } from 'react'
import { router, Link, Head, usePage } from '@inertiajs/react'
import { SlidersHorizontal, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue
} from '@/components/ui/select'
import EventCard from '@/components/EventCard'
import EventListPreview from '@/components/home/EventListPreview'
import EventPagination from '@/components/EventPagination'
import type { EventListItem, SharedProps, PaginationMeta } from '@/types'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

type Filters = {
  q?: string
  cause?: string
  city?: string
  state?: string
  month?: string
  price_min?: string
  price_max?: string
  industry?: string
}

type Props = {
  events: EventListItem[]
  pagination: PaginationMeta
  city_count: number
  filters: Filters
  causes: string[]
  industries: string[]
}

export default function EventsIndex({ events, pagination, city_count, filters, causes, industries }: Props) {
  const { app_url } = usePage<SharedProps>().props
  const ogImage = `${app_url}/meta.png`
  // The first event with a cover is the LCP risk (a placeholder card can't be the
  // LCP), so only that card loads eagerly at high priority; the rest stay lazy.
  const firstImagedIndex = events.findIndex((e) => !!e.cover_image_url)
  const [filterState, setFilterState] = useState<Filters>(filters)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const isInitialMount = useRef(true)

  function updateFilter(key: keyof Filters, value: string) {
    setFilterState((prev) => ({ ...prev, [key]: value }))
  }

  function applyFilters() {
    const params = Object.fromEntries(
      Object.entries(filterState).filter(([, v]) => v && v !== 'all')
    )
    router.get('/events', params, { preserveState: true, preserveScroll: true })
  }

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    const timer = setTimeout(() => {
      const params = Object.fromEntries(
        Object.entries(filterState).filter(([, v]) => v && v !== 'all')
      )
      router.get('/events', params, { preserveState: true, preserveScroll: true, replace: true })
    }, 300)

    return () => clearTimeout(timer)
  }, [filterState])

  function clearFilters() {
    setFilterState({})
    router.get('/events')
  }

  const hasActiveFilters = Object.values(filterState).some((v) => v && v !== 'all')
  const hasAdvancedFilters = Object.entries(filterState).some(([k, v]) => k !== 'q' && v && v !== 'all')

  return (
    <>
      <Head title="Discover Events">
        <meta head-key="description" name="description" content="Discover and attend exclusive charitable gala events. Browse upcoming galas, filter by cause, location, and price." />
        <meta head-key="og:title" property="og:title" content="Discover Events — Gala Season" />
        <meta head-key="og:description" property="og:description" content="Discover and attend exclusive charitable gala events. Browse upcoming galas, filter by cause, location, and price." />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" content={`${app_url}/events`} />
        <meta head-key="og:image" property="og:image" content={ogImage} />
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" content="Discover Events — Gala Season" />
        <meta head-key="twitter:description" name="twitter:description" content="Discover and attend exclusive charitable gala events. Browse upcoming galas, filter by cause, location, and price." />
        <meta head-key="twitter:image" name="twitter:image" content={ogImage} />
      </Head>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          <div className="absolute inset-x-0 top-0 h-[140%] bg-[linear-gradient(to_bottom,oklch(0.85_0.11_62/0.55)_0%,oklch(0.91_0.07_72/0.28)_28%,transparent_62%)]" />
          <div className="absolute -top-1/4 -right-[8%] h-[95%] w-[60%] rounded-full bg-[radial-gradient(circle,oklch(0.80_0.15_55/0.45)_0%,transparent_65%)] blur-2xl" />
        </div>
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8 lg:py-24">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary">
              <span aria-hidden="true">✦</span>
              Discover events
            </p>
            <h1 className="mt-5 text-4xl font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl">
              Find an event worth{' '}
              <span className="font-serif italic text-primary underline decoration-primary/30 decoration-4 underline-offset-4">
                attending
              </span>
              .
            </h1>
            <p className="mt-6 max-w-[48ch] text-lg text-foreground text-pretty">
              Explore charitable events near you by cause, date, and price. Create a philanthropic profile, follow the causes that move you, and step into your role as a changemaker.
            </p>
            <div className="mt-9 flex max-w-xl gap-2">
              <div className="relative flex-1">
                <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search events, causes, cities..."
                  value={filterState.q || ''}
                  onChange={(e) => updateFilter('q', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                  className="pl-9"
                />
              </div>
              <Button onClick={applyFilters}>Search</Button>
            </div>
          </div>
          <div className="lg:pl-8">
            <EventListPreview />
          </div>
        </div>
      </section>

      {/* Events section */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mt-10 mb-8">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">Upcoming Events</h2>
              <p className="mt-1 text-base text-muted-foreground">{pagination.count} events across {city_count} cities</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" asChild>
                <Link href="/organizations/sign_up">Host an Event</Link>
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <Link href="/past-events">Past Events</Link>
              </Button>
            </div>
          </div>

          <div id="events" className="mt-6 flex items-center gap-3">
            <div className="relative max-w-xs flex-1">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search events..." value={filterState.q || ''} onChange={(e) => updateFilter('q', e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()} className="pl-9" />
            </div>
            <Button variant="outline" size="sm" onClick={applyFilters}>Search</Button>
            <Button variant={hasAdvancedFilters ? 'secondary' : 'outline'} size="sm" onClick={() => setFiltersOpen(!filtersOpen)}>
              <SlidersHorizontal className="size-4" />Filters
            </Button>
            {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>}
          </div>
        </div>

        {filtersOpen && (
          <div className="mb-8 space-y-4 rounded-xl bg-muted/50 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Select value={filterState.cause || 'all'} onValueChange={(v) => updateFilter('cause', v)}>
                <SelectTrigger><SelectValue placeholder="All causes" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All causes</SelectItem>
                    {[...causes].sort().map((cause) => <SelectItem key={cause} value={cause}>{cause}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select value={filterState.industry || 'all'} onValueChange={(v) => updateFilter('industry', v)}>
                <SelectTrigger><SelectValue placeholder="All professional networks" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All professional networks</SelectItem>
                    {[...industries].sort().map((industry) => <SelectItem key={industry} value={industry}>{industry}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select value={filterState.month || 'all'} onValueChange={(v) => updateFilter('month', v)}>
                <SelectTrigger><SelectValue placeholder="Any month" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">Any month</SelectItem>
                    {MONTHS.map((name, i) => <SelectItem key={i} value={String(i + 1)}>{name}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Input placeholder="City" value={filterState.city || ''} onChange={(e) => updateFilter('city', e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()} />
              <Input placeholder="State" value={filterState.state || ''} onChange={(e) => updateFilter('state', e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()} />
              <Input type="number" placeholder="Min price" value={filterState.price_min || ''} onChange={(e) => updateFilter('price_min', e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()} />
              <Input type="number" placeholder="Max price" value={filterState.price_max || ''} onChange={(e) => updateFilter('price_max', e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()} />
            </div>
          </div>
        )}

        {events.length === 0 ? (
          <div className="py-20 text-center">
            <h2 className="text-3xl font-semibold tracking-tight">No events match your filters</h2>
            <p className="mt-2 text-muted-foreground">Try adjusting your search or clearing filters.</p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters} className="mt-6">Clear all filters</Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 pb-4 md:grid-cols-2 lg:grid-cols-3 stagger-grid">
              {events.map((event, i) => (
                <Link key={event.id} href={`/events/${event.slug}`} className="block animate-fade-up">
                  <EventCard event={event} showOrganization priority={i === firstImagedIndex} />
                </Link>
              ))}
            </div>
            <EventPagination pagination={pagination} baseUrl="/events" filters={filterState} />
          </>
        )}
      </div>
    </>
  )
}
